import { getWebApp, isInTelegram } from './telegram'
import { PLANNER_STORAGE_KEY, parsePlannerData, type PlannerData } from './planner'

const isLocalPreview = ['127.0.0.1', 'localhost'].includes(window.location.hostname)
const revisions = new Map<string, string>()

export function hasPendingPlannerChanges(): boolean {
  return Boolean(window.localStorage.getItem(`${PLANNER_STORAGE_KEY}.pending`))
}

export function exportPendingPlannerChanges(): void {
  const pending = window.localStorage.getItem(`${PLANNER_STORAGE_KEY}.pending`)
  if (!pending) return
  const url = URL.createObjectURL(new Blob([pending], { type: 'application/json' }))
  const link = document.createElement('a')
  link.href = url
  link.download = 'ORBIT-unsaved-planner.json'
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}

function localData(): PlannerData {
  return parsePlannerData(window.localStorage.getItem(PLANNER_STORAGE_KEY))
}

function saveLocal(data: PlannerData): void {
  window.localStorage.setItem(PLANNER_STORAGE_KEY, JSON.stringify(data))
}

function authHeaders(): Record<string, string> {
  const initData = getWebApp()?.initData ?? ''
  return initData ? { 'X-Telegram-Init-Data': initData } : {}
}

async function request(init?: RequestInit): Promise<Response> {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 10_000)
  try {
    return await fetch('/api/planner', { ...init, signal: controller.signal })
  } catch (error) {
    if (controller.signal.aborted) throw new Error('Сервер не ответил. Проверь связь и повтори синхронизацию.')
    throw error
  } finally {
    window.clearTimeout(timeout)
  }
}

export async function loadPlannerData(): Promise<PlannerData> {
  if (isLocalPreview && !isInTelegram()) return localData()
  const response = await request({ headers: authHeaders(), credentials: 'include', cache: 'no-store' })
  if (!response.ok) throw new Error(response.status === 401 ? 'Открой Mini App из меню бота ORBIT.' : `Ошибка синхронизации: ${response.status}`)
  const payload = await response.json() as { data?: unknown }
  const data = parsePlannerData(JSON.stringify(payload.data))
  const revision = response.headers.get('ETag')
  if (revision) revisions.set(JSON.stringify(data), revision)
  saveLocal(data)
  return data
}

export async function savePlannerData(data: PlannerData, baseline = ''): Promise<void> {
  saveLocal(data)
  if (isLocalPreview && !isInTelegram()) return
  const serialized = JSON.stringify(data)
  window.localStorage.setItem(`${PLANNER_STORAGE_KEY}.pending`, serialized)
  const response = await request({
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(), 'If-Match': revisions.get(baseline) || '' },
    credentials: 'include',
    body: JSON.stringify({ data })
  })
  if (!response.ok) throw new Error(response.status === 409 ? 'Планер изменён на другом устройстве. Твои правки сохранены локально. Не закрывай приложение: требуется сверка изменений.' : response.status === 401 ? 'Сессия Telegram истекла. Переоткрой приложение.' : `Ошибка сохранения: ${response.status}`)
  const revision = response.headers.get('ETag')
  if (revision) revisions.set(JSON.stringify(data), revision)
  if (window.localStorage.getItem(`${PLANNER_STORAGE_KEY}.pending`) === serialized) window.localStorage.removeItem(`${PLANNER_STORAGE_KEY}.pending`)
}
